import i18n from 'i18next';

import {apiClient} from 'core/apiClient';
import {CODIFIERS_LINK} from 'core/constants/codifiers';
import {
    DocumentCommissioningData,
    DocumentDropOutData,
    DocumentMovingData,
    DropoutDictionaryResponse,
    GtinDetailRaw,
} from 'types/documents';

export type StorageLocationStatus = 'active' | 'deactivated';

export interface ProfileStorageLocation {
    id: number;
    storageName: string | null;
    status: StorageLocationStatus;
}

export async function fetchDocumentDropOutCreate(params: DocumentDropOutData) {
    const {attachmentFiles = [], supportingDocumentDate, ...payload} = params;
    const attachment = await Promise.all(
        attachmentFiles.map(async (file) => {
            const formData = new FormData();
            formData.append(
                'documentNumber',
                new Blob([JSON.stringify(params.documentId)], {type: 'application/json'}),
            );
            formData.append('file', file);

            return (
                await apiClient.post<{documentKey: string}>(
                    '/marking/documents/file/upload',
                    formData,
                    {baseURL: '/api/v2'},
                )
            ).data.documentKey;
        }),
    );
    const data = await apiClient.post(
        '/marking/dropout',
        {
            ...payload,
            supportingDocumentDate: supportingDocumentDate?.toISOString(),
            ...(attachment.length ? {attachment} : {}),
        },
        {baseURL: '/api/v2'},
    );
    return {data};
}

export async function fetchDropoutDictionary(): Promise<DropoutDictionaryResponse> {
    return (
        await apiClient.get<DropoutDictionaryResponse>('/marking/dictionary/dropout', {
            baseURL: '/api/v2',
        })
    ).data;
}
export async function fetchDocumentMovingCreate(params: DocumentMovingData) {
    const data = await apiClient.post('/marking/moving', params);
    return {data};
}

export async function fetchDocumentCommissioningCreate(params: DocumentCommissioningData) {
    const data = await apiClient.post('/marking/commissioning', params);
    return {data};
}
export async function fetchDocumentUPDCreate(data: string | null) {
    const response = await apiClient.post('/marking/upd', data?.toString(), {
        headers: {'Content-Type': 'application/json'},
    });
    return {
        response,
    };
}

export async function fetchDocumentGtinInfo(codes: Array<string>): Promise<GtinDetailRaw[]> {
    const gtins = codes.map((item) => {
        if (/[0-9]/.test(item)) return item;
        return item
            .match(/01(.*?)21/g)?.[0]
            ?.replace(/01/, '')
            .replace(/21$/, '');
    });
    return (await apiClient.post(`/service/product-cards/by-marking-codes`, {markingCodes: gtins}))
        .data;
}

export async function fetchStorages(userId?: string | number) {
    const params = typeof userId === 'string' || typeof userId === 'number' ? {userId} : undefined;

    return (
        await apiClient.get<{storageLocations: ProfileStorageLocation[]}>(
            'service/profile/storage-locations',
            params ? {params} : undefined,
        )
    ).data;
}

export async function fetchDocumentInvoiceCreate(params: string) {
    const formData = new FormData();
    const file = new Blob([params], {type: 'application/xml'});

    formData.append('file', file, 'invoice.xml');

    const data = await apiClient.post('/marking/invoice/load', formData, {
        baseURL: '/api/v2',
    });
    return {data};
}

export async function fetchDocumentInvoiceXmlValidation(params: string) {
    const data = await apiClient.post('/marking/invoice/validate', params, {
        headers: {'Content-Type': 'application/xml'},
    });
    return {data};
}

export async function fetchDocumentInvoiceQR(irn: string) {
    return (await apiClient.get<string>('/marking/invoice/qr', {params: {irn}})).data;
}

export async function fetchInvoiceIrn() {
    const data = await apiClient.get('/service/invoice/generation-irn');
    return {data: data.data};
}

export async function fetchCodeForServiceOrProducts(params: {
    isService: boolean;
    params: {
        code: string;
        size: number;
        number: number;
    };
}): Promise<any> {
    const url = params.isService
        ? `${CODIFIERS_LINK}/api/v1/hsn/v2/search`
        : `${CODIFIERS_LINK}/api/v1/unspsc/search`;
    const data: any = await apiClient.get(url, {
        params: {
            query: params.params.code,
            size: params.params.size,
            number: params.params.number,
            sortDirection: 'DESC',
            lang: i18n.language,
        },
    });
    return data.data;
}

export async function fetchDocumentUtilisationBatchNumber(params: {productionDate: string}) {
    return (
        await apiClient.get<string>('/marking/utilisation/gen-batch-number', {
            params: {productionDate: params.productionDate},
        })
    ).data;
}

export async function fetchDocumentGenerateId() {
    return (
        await apiClient.get<string>('/marking/documents/generate-id', {
            baseURL: '/api/v2',
        })
    ).data;
}
